/** InputLayer. PointerEvents → GestureFrame. No audio. No visual. */

import type { FingerState, GestureFrame } from '../types';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_FINGERS   = 5;
const TAP_MS        = 200;
const TAP_PX        = 8;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_PX = 40;
const HOLD_MS       = 400;

// ─── Internal types ───────────────────────────────────────────────────────────

interface FingerInternal extends FingerState {
  startX:     number;
  startY:     number;
  totalMove:  number;     // cumulative px movement (raw client coords)
  lastClientX: number;
  lastClientY: number;
  lastMoveAt:  number;    // timestamp of last pointermove
}

interface PendingTap {
  x: number;
  y: number;
  at: number;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeAngle(delta: number): number {
  // Normalize angle delta to [-π, π]
  let d = delta % (2 * Math.PI);
  if (d > Math.PI)  d -= 2 * Math.PI;
  if (d < -Math.PI) d += 2 * Math.PI;
  return d;
}

function dist(ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  return Math.sqrt(dx * dx + dy * dy);
}

// ─── InputLayer ───────────────────────────────────────────────────────────────

export class InputLayer {
  // Public read-only fingers map
  get fingers(): ReadonlyMap<number, FingerState> { return this._fingers; }

  private readonly _fingers = new Map<number, FingerInternal>();
  private readonly target: HTMLElement;

  // Config
  private readonly maxFingers:   number;
  private readonly tapMs:        number;
  private readonly doubleTapMs:  number;
  private readonly doubleTapPx:  number;
  private readonly holdMs:       number;

  // Bounding rect (normalized coordinate origin)
  private rect: DOMRect;

  // One-shot queues
  private pendingTaps:       PendingTap[] = [];
  private pendingDoubleTaps: PendingTap[] = [];

  // Last tap info for double-tap detection
  private lastTap: PendingTap | null = null;

  // Pinch/rotate state for delta computation
  private lastPinchDist:  number | null = null;
  private lastPinchAngle: number | null = null;

  // Bound handlers (kept for add/remove symmetry)
  private readonly _onDown:   (e: PointerEvent) => void;
  private readonly _onMove:   (e: PointerEvent) => void;
  private readonly _onUp:     (e: PointerEvent) => void;
  private readonly _onCancel: (e: PointerEvent) => void;
  private readonly _onResize: () => void;

  private attached = false;

  constructor(opts: {
    target:       HTMLElement;
    maxFingers?:  number;
    tapMs?:       number;
    doubleTapMs?: number;
    doubleTapPx?: number;
    holdMs?:      number;
  }) {
    this.target      = opts.target;
    this.maxFingers  = opts.maxFingers  ?? MAX_FINGERS;
    this.tapMs       = opts.tapMs       ?? TAP_MS;
    this.doubleTapMs = opts.doubleTapMs ?? DOUBLE_TAP_MS;
    this.doubleTapPx = opts.doubleTapPx ?? DOUBLE_TAP_PX;
    this.holdMs      = opts.holdMs      ?? HOLD_MS;

    this.rect = this.target.getBoundingClientRect();

    this._onDown   = this._handleDown.bind(this);
    this._onMove   = this._handleMove.bind(this);
    this._onUp     = this._handleUp.bind(this);
    this._onCancel = this._handleUp.bind(this);   // pointercancel ≡ pointerup
    this._onResize = this._refreshRect.bind(this);
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────────

  attach(): void {
    if (this.attached) return;
    this.attached = true;
    this._refreshRect();
    this.target.addEventListener('pointerdown',   this._onDown,   { passive: false });
    this.target.addEventListener('pointermove',   this._onMove,   { passive: false });
    this.target.addEventListener('pointerup',     this._onUp,     { passive: false });
    this.target.addEventListener('pointercancel', this._onCancel, { passive: false });
    window.addEventListener('resize', this._onResize);
  }

  detach(): void {
    if (!this.attached) return;
    this.attached = false;
    this.target.removeEventListener('pointerdown',   this._onDown);
    this.target.removeEventListener('pointermove',   this._onMove);
    this.target.removeEventListener('pointerup',     this._onUp);
    this.target.removeEventListener('pointercancel', this._onCancel);
    window.removeEventListener('resize', this._onResize);
  }

  dispose(): void {
    this.detach();
    this._fingers.clear();
    this.pendingTaps       = [];
    this.pendingDoubleTaps = [];
    this.lastTap           = null;
    this.lastPinchDist     = null;
    this.lastPinchAngle    = null;
  }

  // ─── flush ──────────────────────────────────────────────────────────────────

  flush(now: number): GestureFrame {
    // 1. Update ageMs for every active finger
    for (const f of this._fingers.values()) {
      f.ageMs = now - f.startedAt;
    }

    // 2. Pinch + rotate (exactly 2 fingers, deterministic by lowest pointerIds)
    let pinch:  GestureFrame['pinch']  = null;
    let rotate: GestureFrame['rotate'] = null;

    if (this._fingers.size === 2) {
      const [a, b] = this._getSortedTwoFingers();

      // Use raw client coords for distance/angle computation to avoid
      // rect-size effects on angle math; then normalize distance by rect width.
      const rawDist  = dist(a.lastClientX, a.lastClientY, b.lastClientX, b.lastClientY);
      const rawAngle = Math.atan2(b.lastClientY - a.lastClientY, b.lastClientX - a.lastClientX);

      const distDelta  = this.lastPinchDist  !== null ? rawDist  - this.lastPinchDist  : 0;
      const angleDelta = this.lastPinchAngle !== null ? normalizeAngle(rawAngle - this.lastPinchAngle) : 0;

      pinch  = { distance: rawDist,  delta: distDelta  };
      rotate = { angle:    rawAngle, delta: angleDelta  };

      this.lastPinchDist  = rawDist;
      this.lastPinchAngle = rawAngle;
    } else {
      // Reset pinch/rotate baseline when finger count changes
      this.lastPinchDist  = null;
      this.lastPinchAngle = null;
    }

    // 3. Holds: fingers still down for ≥ holdMs with totalMove < TAP_PX
    const holds: GestureFrame['holds'] = [];
    for (const f of this._fingers.values()) {
      if (f.ageMs >= this.holdMs && f.totalMove < TAP_PX) {
        holds.push({ id: f.id, x: f.x, y: f.y, durationMs: f.ageMs });
      }
    }

    // 4. Snapshot fingers map (shallow copy — values are objects, sufficient for external reads)
    const fingerSnapshot = new Map<number, FingerState>(this._fingers);

    // 5. Drain one-shot queues
    const taps       = this.pendingTaps;
    const doubleTaps = this.pendingDoubleTaps;
    this.pendingTaps       = [];
    this.pendingDoubleTaps = [];

    return { fingers: fingerSnapshot, pinch, rotate, taps, doubleTaps, holds };
  }

  // ─── Event handlers ─────────────────────────────────────────────────────────

  private _handleDown(e: PointerEvent): void {
    // 5-finger cap: 6th+ pointerdown is a true no-op
    if (this._fingers.size >= this.maxFingers) return;

    const { nx, ny } = this._normalize(e.clientX, e.clientY);
    // Always use performance.now() — e.timeStamp uses a different epoch in jsdom
    // and is inconsistent with flush(now) callers who pass performance.now().
    const now = performance.now();

    const finger: FingerInternal = {
      id:          e.pointerId,
      x:           nx,
      y:           ny,
      vx:          0,
      vy:          0,
      ageMs:       0,
      startedAt:   now,
      startX:      e.clientX,
      startY:      e.clientY,
      totalMove:   0,
      lastClientX: e.clientX,
      lastClientY: e.clientY,
      lastMoveAt:  now,
    };

    this._fingers.set(e.pointerId, finger);
  }

  private _handleMove(e: PointerEvent): void {
    const f = this._fingers.get(e.pointerId);
    if (!f) return;

    const now = performance.now();
    // Use a minimum dt of 1ms to avoid division by zero when two events fire
    // within the same tick (common in synchronous test sequences).
    const dt  = Math.max(now - f.lastMoveAt, 1);

    const { nx, ny } = this._normalize(e.clientX, e.clientY);

    const dxPx = e.clientX - f.lastClientX;
    const dyPx = e.clientY - f.lastClientY;

    f.vx = dxPx / dt;
    f.vy = dyPx / dt;

    f.totalMove  += Math.sqrt(dxPx * dxPx + dyPx * dyPx);
    f.x           = nx;
    f.y           = ny;
    f.lastClientX = e.clientX;
    f.lastClientY = e.clientY;
    f.lastMoveAt  = now;
  }

  private _handleUp(e: PointerEvent): void {
    const f = this._fingers.get(e.pointerId);
    if (!f) return;

    const now      = performance.now();
    const duration = now - f.startedAt;

    // Tap detection: short + small movement
    if (duration < this.tapMs && f.totalMove < TAP_PX) {
      const tap: PendingTap = { x: f.x, y: f.y, at: now };
      this.pendingTaps.push(tap);

      // DoubleTap detection
      if (this.lastTap !== null) {
        const timeDiff = now - this.lastTap.at;
        const spaceDiff = dist(
          tap.x * this.rect.width,  tap.y * this.rect.height,
          this.lastTap.x * this.rect.width, this.lastTap.y * this.rect.height,
        );
        if (timeDiff < this.doubleTapMs && spaceDiff <= this.doubleTapPx) {
          this.pendingDoubleTaps.push(tap);
          this.lastTap = null;  // consumed
        } else {
          this.lastTap = tap;
        }
      } else {
        this.lastTap = tap;
      }
    }

    this._fingers.delete(e.pointerId);
  }

  // ─── Internals ──────────────────────────────────────────────────────────────

  private _normalize(clientX: number, clientY: number): { nx: number; ny: number } {
    const { left, top, width, height } = this.rect;
    return {
      nx: width  > 0 ? (clientX - left) / width  : 0,
      ny: height > 0 ? (clientY - top)  / height : 0,
    };
  }

  private _refreshRect(): void {
    this.rect = this.target.getBoundingClientRect();
  }

  private _getSortedTwoFingers(): [FingerInternal, FingerInternal] {
    const entries = [...this._fingers.entries()].sort((a, b) => a[0] - b[0]);
    return [entries[0][1], entries[1][1]];
  }
}
